import LoadingButtonProvider from '@/components/buttons/LoadingButtonProvider';
import { QuickLoginModalHOC } from '@/components/modal/LoginModal2HOC';
import { DoccenAiSubscriptionsBenefit } from '@/constant/subscription';
import useToast from '@/hooks/useToast';
import useToggle from '@/hooks/useToggle';
import { ESubscriptionPackage } from '@/interfaces/subscription.interface';
import {
  checkSubscriptionResource,
  hasUserSubscriptedIdealPlan,
} from 'common/subscription/checkSubscriptionResource';
import { Button } from 'design-system/components/Button';
import { Divider } from 'design-system/components/Divider';
import dynamic from 'next/dynamic';
import { useContext } from 'react';
import { DocumentDetailContext } from 'state/document/DocumentDetailProvider';
import { twMerge } from 'tailwind-merge';
import LibraryMaterialDetailReadySection from '../components/LibraryMaterialDetailReadySection';

const DynamicDrawer = dynamic(() => import('@mui/material/Drawer'));
const DocumentDetailMainSection = dynamic(
  () => import('./components/DocumentDetailMainSection')
);
const LibraryMaterialDetailUpgradePlanSection = dynamic(
  () => import('../components/LibraryMaterialDetailUpgradePlanSection')
);
const Typography = dynamic(
  () => import('design-system/components/Typography/Typography')
);
const Icon = dynamic(() => import('design-system/components/Icon/Icon'));

const DocumentDetailBottomSheet = () => {
  const { latestSubscription, document, mutationResult } = useContext(
    DocumentDetailContext
  );

  const toast = useToast();
  const { isLoading, mutate: handleProcessAI } = mutationResult;

  const maximumDocumentPageLimitCondition =
    document &&
    document.material.metadata.pageCount >
      DoccenAiSubscriptionsBenefit[ESubscriptionPackage.Premium].document
        .maxPageCountForAI;

  const maxiumDocumentSizeLimitCondition =
    document &&
    document.material.metadata.size >
      DoccenAiSubscriptionsBenefit[ESubscriptionPackage.Premium].document
        .maxFileSizeForAI;

  const doesExceedLimit =
    maximumDocumentPageLimitCondition || maxiumDocumentSizeLimitCondition;

  const doesUserHasAccessToDocument =
    checkSubscriptionResource(latestSubscription).document.accessToDocument ||
    document?.material.isOwner;

  return (
    <>
      <div
        style={{
          background: !doesUserHasAccessToDocument
            ? 'linear-gradient(180deg, #FFF 0%, #FFF9E9 100%)'
            : '',
        }}
        className={twMerge(
          'fixed bottom-[74px] left-1/2 z-10 w-[calc(100dvw-16px)] -translate-x-1/2 rounded-[10px] border shadow-elevator2',
          doesUserHasAccessToDocument
            ? 'border-Success/Border bg-Success/Main'
            : 'border-Other/OutlinedUpgradedborder-stroke bg-white'
        )}
      >
        <DocumentDetailPopDrawerButton
          doesUserHasAccessToDocument={doesUserHasAccessToDocument}
        />
        <div className="flex items-center justify-between p-2">
          {doesUserHasAccessToDocument ? (
            <LibraryMaterialDetailReadySection className="bg-transparent p-0 shadow-none" />
          ) : (
            <LibraryMaterialDetailUpgradePlanSection />
          )}

          <div className="flex items-center justify-between gap-2 px-1">
            <LoadingButtonProvider
              className="rounded-full"
              isLoading={isLoading}
            >
              <QuickLoginModalHOC
                disabled={
                  !document?.material.metadata.pageCount || doesExceedLimit
                }
                handleClickWhenDisabledQuickLogin={() => handleProcessAI()}
              >
                <Button
                  size="small"
                  className="bg-primary_blue_bg"
                  startIcon={{
                    name: 'blink-blink',
                  }}
                  disabled={
                    !document?.material.metadata.pageCount || doesExceedLimit
                  }
                >
                  DoccenAI
                </Button>
              </QuickLoginModalHOC>
            </LoadingButtonProvider>
            <QuickLoginModalHOC>
              <Button
                icon={{
                  name: 'save',
                }}
                mode="icon-only"
                size="small"
                onClick={() => toast('Tính năng đang phát triển', 'success')}
              />
            </QuickLoginModalHOC>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentDetailBottomSheet;

const DocumentDetailPopDrawerButton = ({
  doesUserHasAccessToDocument,
}: {
  doesUserHasAccessToDocument: boolean | undefined;
}) => {
  const { latestSubscription, document } = useContext(DocumentDetailContext);
  const hasUserSubscriptedIdealPlanValue =
    hasUserSubscriptedIdealPlan(latestSubscription) ||
    document?.material.isOwner;

  const [openDrawer, toggleDrawer] = useToggle();

  return (
    <>
      <Button
        size="small"
        mode="icon-only"
        icon={{
          name: 'chevron-up',
        }}
        className="absolute -top-5 left-1/2 -translate-x-1/2"
        onClick={toggleDrawer}
      />
      {openDrawer && (
        <DynamicDrawer
          anchor="bottom"
          open={openDrawer}
          onClose={toggleDrawer}
          sx={{
            '.MuiPaper-root': {
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px',
            },
            zIndex: 70,
          }}
        >
          <div
            style={{
              background: !hasUserSubscriptedIdealPlanValue
                ? 'linear-gradient(180deg, #FFF 0%, #FFF9E9 100%)'
                : '',
            }}
            className={twMerge('rounded-lg bg-white')}
          >
            <div className="flex items-center justify-between p-4">
              <Typography variant="heading2">Thông tin tài liệu</Typography>
              <Icon
                iconName="close-symbol"
                className="cursor-pointer"
                onClick={toggleDrawer}
              />
            </div>
            {doesUserHasAccessToDocument && (
              <LibraryMaterialDetailReadySection
                icon={<Icon iconName={'done'} size={32} />}
                className="mx-2 mb-6 mt-2"
              />
            )}
            <div className={twMerge('flex h-fit flex-col gap-6 px-6 pb-6')}>
              {!hasUserSubscriptedIdealPlanValue && (
                <LibraryMaterialDetailUpgradePlanSection />
              )}
              <Divider />
              <DocumentDetailMainSection />
            </div>
          </div>
        </DynamicDrawer>
      )}
    </>
  );
};
